import { createFileRoute, notFound } from "@tanstack/react-router";
import {
  Package, ShoppingCart, Factory, Truck, FileText, BarChart3, Users,
  Workflow, Lock, Database, GraduationCap, Activity, Layers, Shield,
  Zap, Gauge, Plug, RefreshCw, Calculator, ClipboardList, Search, Wrench,
} from "lucide-react";
import { ServicePage, type ServicePageData } from "@/components/sections/service-page";

const CHILDREN: Record<string, ServicePageData & { metaDesc: string }> = {
  "inventory-warehouse": {
    eyebrow: "ERP · Modules",
    title: "Inventory & Warehouse",
    sub: "Multi-warehouse stock, barcode scanning and real-time inventory accuracy.",
    metaDesc: "ERP inventory and warehouse management: multi-warehouse, barcode/RFID, batch/serial tracking and stock reconciliation.",
    intro:
      "We implement inventory and warehouse modules that give you real-time stock accuracy across every location — barcode/RFID scanning, batch and serial tracking, bin management, cycle counts, and automated reorder rules.",
    features: [
      { icon: Package, title: "Multi-warehouse", description: "Stock visibility across locations with inter-warehouse transfers." },
      { icon: Search, title: "Barcode + RFID", description: "Mobile scanning for receiving, picking, and stock takes." },
      { icon: Layers, title: "Batch + serial", description: "Lot tracking, expiry dates, and serial-number genealogy." },
      { icon: ClipboardList, title: "Bin management", description: "Putaway rules, pick paths, and zone-based storage." },
      { icon: RefreshCw, title: "Reorder rules", description: "Min/max, lead-time-aware, and supplier-specific reordering." },
      { icon: Activity, title: "Stock accuracy", description: "Cycle counts, variance reports, and reconciliation tools." },
    ],
    process: [
      { step: "01", title: "Map", description: "Warehouses, bins, items, and current accuracy baseline." },
      { step: "02", title: "Configure", description: "Hierarchies, scanning workflows, and reorder rules." },
      { step: "03", title: "Migrate", description: "Item master and opening balances from legacy system." },
      { step: "04", title: "Adopt", description: "Mobile scanning training and accuracy KPIs live." },
    ],
    deliverables: ["Configured warehouse hierarchy", "Item master + UoM setup", "Mobile scanning workflows", "Reorder rules", "Cycle count program", "Training + SOPs"],
    techStack: ["ERPNext", "Odoo", "Honeywell / Zebra scanners", "Mobile WMS", "Supabase"],
    pricing: [
      { name: "Single warehouse", price: "$6k", features: ["Setup + scanning", "Reorder rules", "Migration", "3 weeks"] },
      { name: "Multi-warehouse", price: "$18k", period: "project", highlighted: true, features: ["Transfers + bins", "Batch/serial", "Cycle counts", "6–8 weeks"] },
      { name: "WMS retainer", price: "$2.5k", period: "month", features: ["Accuracy tuning", "Process improvements", "Support"] },
    ],
    faqs: [
      { q: "Will scanning work offline?", a: "Yes — mobile devices queue scans offline and sync when back online." },
      { q: "Lot expiry and FEFO?", a: "Fully supported, with alerts for near-expiry stock." },
    ],
  },
  "sales-purchase": {
    eyebrow: "ERP · Modules",
    title: "Sales & Purchase",
    sub: "Quote-to-cash and procure-to-pay flows tied to inventory and accounting.",
    metaDesc: "ERP sales and purchase modules: quote-to-cash, procure-to-pay, approvals and supplier management.",
    intro:
      "We set up sales and purchase workflows end-to-end — quotes, orders, deliveries, invoices on the sales side; RFQs, POs, GRNs, supplier invoices on the purchase side — all wired into inventory and the ledger automatically.",
    features: [
      { icon: ShoppingCart, title: "Quote-to-cash", description: "Quotes, orders, deliveries, invoices, returns and credits." },
      { icon: ClipboardList, title: "Procure-to-pay", description: "RFQs, POs, GRNs, supplier invoices, three-way match." },
      { icon: Users, title: "Customer + supplier portals", description: "Self-service quote acceptance and PO acknowledgment." },
      { icon: Workflow, title: "Approval workflows", description: "Threshold-based approvals with audit trail." },
      { icon: Calculator, title: "Price lists + discounts", description: "Tiered pricing, promotions, and contract pricing." },
      { icon: Activity, title: "Live margins", description: "Per-order cost and margin visibility in real time." },
    ],
    process: [
      { step: "01", title: "Map flows", description: "Existing sales/purchase processes and approval matrix." },
      { step: "02", title: "Configure", description: "Workflows, price lists, approvals, and tax rules." },
      { step: "03", title: "Connect", description: "CRM, e-commerce, EDI, and supplier integrations." },
      { step: "04", title: "Adopt", description: "Sales + purchasing team training and KPI dashboards." },
    ],
    deliverables: ["S&P workflow configuration", "Approval matrix + roles", "Customer/supplier portals", "Price list + promo setup", "Integrations (CRM, EDI)", "Training + SOPs"],
    techStack: ["ERPNext", "Odoo", "HubSpot / Salesforce", "Shopify", "Stripe", "EDI"],
    pricing: [
      { name: "Essentials", price: "$5k", features: ["Sales + purchase basics", "Email approvals", "Stripe + email"] },
      { name: "Full S&P", price: "$15k", period: "project", highlighted: true, features: ["Portals + approvals", "Pricing engine", "Integrations"] },
      { name: "S&P retainer", price: "$2.5k", period: "month", features: ["Continuous improvements", "New connectors", "Support"] },
    ],
    faqs: [
      { q: "Do you handle EDI?", a: "Yes — for retailers and large customers using 850/810/856 etc." },
      { q: "Can we have customer-specific pricing?", a: "Yes — by customer, group, contract, or rule-based pricing engine." },
    ],
  },
  "manufacturing": {
    eyebrow: "ERP · Modules",
    title: "Manufacturing",
    sub: "BOMs, work orders, shop-floor and capacity planning for make-to-stock and make-to-order.",
    metaDesc: "ERP manufacturing module: BOMs, routings, work orders, shop floor, MRP and capacity planning.",
    intro:
      "We implement manufacturing modules for discrete, process, and mixed-mode shops — multi-level BOMs, routings, work orders, shop-floor execution, MRP, and capacity planning with real-time WIP and yield reporting.",
    features: [
      { icon: Factory, title: "BOMs + routings", description: "Multi-level BOMs, alternate routings, and version control." },
      { icon: ClipboardList, title: "Work orders", description: "Issue, execute, complete with real-time WIP tracking." },
      { icon: Gauge, title: "Shop floor", description: "Operator screens, time + qty capture, downtime reasons." },
      { icon: Workflow, title: "MRP + planning", description: "Net requirements, lead-time-aware, and supplier-aware." },
      { icon: Activity, title: "Capacity planning", description: "Work center loading and finite/infinite scheduling." },
      { icon: BarChart3, title: "Yield + scrap", description: "Per-order yield, scrap reasons, and rework tracking." },
    ],
    process: [
      { step: "01", title: "Discover", description: "Product structure, routings, and shop-floor reality." },
      { step: "02", title: "Configure", description: "BOMs, work centers, MRP rules, and shop screens." },
      { step: "03", title: "Pilot", description: "Single product family before company-wide rollout." },
      { step: "04", title: "Scale", description: "Full rollout with operator training and KPIs." },
    ],
    deliverables: ["BOMs + routings setup", "Work order workflows", "Shop floor terminals", "MRP + planning config", "Yield + downtime dashboards", "Operator training"],
    techStack: ["ERPNext", "Odoo Manufacturing", "Custom shop terminals", "MES integrations", "PostgreSQL"],
    pricing: [
      { name: "Pilot line", price: "$10k", features: ["1 product family", "BOMs + work orders", "Basic MRP", "4 weeks"] },
      { name: "Full plant", price: "$35k", period: "project", highlighted: true, features: ["Multi-line", "Shop floor + MRP", "Capacity planning", "10–14 weeks"] },
      { name: "Mfg retainer", price: "$4k", period: "month", features: ["Continuous improvements", "Yield projects", "Support"] },
    ],
    faqs: [
      { q: "Discrete or process manufacturing?", a: "Both — we configure differently for each, and support mixed-mode shops." },
      { q: "MES integration?", a: "Yes — we connect to existing MES/SCADA or implement lightweight shop terminals." },
    ],
  },
  "supply-chain": {
    eyebrow: "ERP · Modules",
    title: "Supply Chain",
    sub: "Demand planning, supplier management, logistics and traceability end-to-end.",
    metaDesc: "ERP supply chain: demand planning, supplier management, logistics, 3PL integrations and traceability.",
    intro:
      "We connect every link — demand planning, supplier collaboration, logistics, 3PL integration, and end-to-end traceability — so you can see where things are, predict where they need to be, and act before exceptions become fires.",
    features: [
      { icon: Truck, title: "Demand planning", description: "Statistical forecasts blended with sales pipeline." },
      { icon: Users, title: "Supplier collaboration", description: "Portals for PO ack, ASN, and supplier scorecards." },
      { icon: Package, title: "Logistics + 3PL", description: "Carrier integrations, label generation, tracking ingestion." },
      { icon: Search, title: "Track + trace", description: "Lot-level traceability for recalls and compliance." },
      { icon: Activity, title: "Exception dashboards", description: "Late shipments, stock-out risk, and supplier SLAs." },
      { icon: Plug, title: "EDI + API", description: "EDI 850/855/856/810 and modern REST integrations." },
    ],
    process: [
      { step: "01", title: "Map chain", description: "Suppliers, carriers, 3PLs, and current data flows." },
      { step: "02", title: "Configure", description: "Forecasting model, portals, and carrier integrations." },
      { step: "03", title: "Integrate", description: "EDI, API, and 3PL connections with reconciliation." },
      { step: "04", title: "Operate", description: "Daily exception dashboards and continuous improvement." },
    ],
    deliverables: ["Forecasting setup", "Supplier portal", "Carrier + 3PL integrations", "Lot traceability", "Exception dashboards", "Supplier scorecards"],
    techStack: ["ERPNext", "Odoo", "ShipStation / EasyPost", "FedEx / UPS / DHL APIs", "EDI", "Tableau"],
    pricing: [
      { name: "Essentials", price: "$8k", features: ["Forecasting basics", "1 carrier", "Supplier portal", "4 weeks"] },
      { name: "Full SCM", price: "$25k", period: "project", highlighted: true, features: ["Multi-carrier", "EDI + 3PL", "Trace + dashboards", "8–10 weeks"] },
      { name: "SCM retainer", price: "$3.5k", period: "month", features: ["Connector maintenance", "Forecast tuning", "New flows"] },
    ],
    faqs: [
      { q: "Do you handle customs?", a: "Yes — we integrate with brokers and generate HS-code documentation." },
      { q: "Forecasting accuracy?", a: "Typically 15–25% MAPE improvement after 3 months of tuning." },
    ],
  },
  "accounting-invoicing": {
    eyebrow: "ERP · Finance",
    title: "Accounting & Invoicing",
    sub: "Double-entry ledger, multi-currency, tax handling and automated invoicing.",
    metaDesc: "ERP accounting and invoicing: chart of accounts, AR/AP, multi-currency, tax compliance and auto-invoicing.",
    intro:
      "We configure accounting on the ERP that finance teams actually want to use — proper double-entry ledger, multi-currency, tax compliance, AR/AP automation, bank reconciliation, and one-click invoicing tied to sales orders.",
    features: [
      { icon: Calculator, title: "Chart of accounts", description: "Industry-aligned COA with multi-company consolidation." },
      { icon: FileText, title: "Auto-invoicing", description: "Order-to-invoice in one click with PDF + email delivery." },
      { icon: RefreshCw, title: "Bank reconciliation", description: "Bank feeds, rule-based matching, and audit trail." },
      { icon: Shield, title: "Tax compliance", description: "Sales tax, VAT/GST, withholding, and e-invoicing where required." },
      { icon: Workflow, title: "AR / AP automation", description: "Dunning, payment reminders, supplier payment runs." },
      { icon: Activity, title: "Period close", description: "Checklists, accruals, and 3-day month-end close." },
    ],
    process: [
      { step: "01", title: "COA design", description: "Map current chart and reporting needs." },
      { step: "02", title: "Configure", description: "Tax rules, banks, AR/AP workflows, and roles." },
      { step: "03", title: "Migrate", description: "Open AR/AP and trial balance with reconciliation." },
      { step: "04", title: "Close cycle", description: "First month-end close together with the team." },
    ],
    deliverables: ["Chart of accounts", "AR/AP automation", "Bank feeds + reconciliation rules", "Tax + e-invoicing setup", "Month-end checklist", "Migration + opening balances"],
    techStack: ["ERPNext", "Odoo Accounting", "Plaid", "Avalara", "Stripe", "QuickBooks migration"],
    pricing: [
      { name: "Essentials", price: "$5k", features: ["COA + AR/AP", "1 bank feed", "Tax basics", "3 weeks"] },
      { name: "Full finance", price: "$15k", period: "project", highlighted: true, features: ["Multi-currency", "Multi-company", "Auto-reconcile", "6–8 weeks"] },
      { name: "Finance retainer", price: "$2.5k", period: "month", features: ["Month-end help", "Reports + tweaks", "Compliance updates"] },
    ],
    faqs: [
      { q: "Will it satisfy our auditor?", a: "Yes — proper double-entry, audit trail, and segregation of duties." },
      { q: "E-invoicing for KSA / India / Italy?", a: "Yes — we handle country-specific e-invoicing requirements." },
    ],
  },
  "reporting-analytics": {
    eyebrow: "ERP · Finance",
    title: "Reporting & Analytics",
    sub: "Live dashboards, financial reports, and ad-hoc analytics on top of your ERP.",
    metaDesc: "ERP reporting and analytics: live dashboards, financial statements, KPI scorecards and self-serve BI.",
    intro:
      "We turn ERP data into decisions — pixel-perfect financial statements, live operational dashboards, KPI scorecards, and self-serve BI so your team stops exporting CSVs and starts answering questions live.",
    features: [
      { icon: BarChart3, title: "Live dashboards", description: "Cash, sales, inventory, and operations dashboards." },
      { icon: FileText, title: "Financial statements", description: "P&L, balance sheet, cash flow with drill-down." },
      { icon: Gauge, title: "KPI scorecards", description: "Targets vs actuals with trend and variance analysis." },
      { icon: Search, title: "Self-serve BI", description: "Metabase / Superset on top of your ERP warehouse." },
      { icon: Workflow, title: "Scheduled reports", description: "Email / Slack delivery of recurring reports." },
      { icon: Database, title: "Warehouse + ETL", description: "Optional warehouse with dbt for advanced modeling." },
    ],
    process: [
      { step: "01", title: "KPI workshop", description: "Define what matters and who needs to see it." },
      { step: "02", title: "Model", description: "Data model, metrics layer, and report design." },
      { step: "03", title: "Build", description: "Dashboards, scorecards, and report templates." },
      { step: "04", title: "Enable", description: "Self-serve training and adoption tracking." },
    ],
    deliverables: ["KPI catalog", "Live dashboards", "Financial report templates", "Self-serve BI setup", "Scheduled report jobs", "Training + docs"],
    techStack: ["Metabase", "Superset", "Tableau", "dbt", "Postgres", "BigQuery", "Looker"],
    pricing: [
      { name: "Essentials", price: "$4k", features: ["10 dashboards", "Core financials", "Email schedules", "2 weeks"] },
      { name: "Full BI", price: "$14k", period: "project", highlighted: true, features: ["Warehouse + dbt", "Self-serve BI", "Training", "5–7 weeks"] },
      { name: "BI retainer", price: "$2.5k", period: "month", features: ["New dashboards", "Metric tuning", "Adoption support"] },
    ],
    faqs: [
      { q: "Real-time or daily refresh?", a: "Operational dashboards: live. Financial / large warehouse: hourly or daily — by design." },
      { q: "Looker / Tableau / Metabase?", a: "We pick based on budget and skills; Metabase for fast value, Looker for scale." },
    ],
  },
  "payroll": {
    eyebrow: "ERP · Finance",
    title: "Payroll",
    sub: "Compliant, automated payroll integrated with HR, time-tracking and accounting.",
    metaDesc: "ERP payroll module: salary structures, tax compliance, time integration, payslips and statutory filings.",
    intro:
      "We set up payroll that finance and HR can trust — salary structures, statutory deductions, tax compliance, integration with time and attendance, automated payslips, and clean journal entries straight to the ledger.",
    features: [
      { icon: Users, title: "Salary structures", description: "Components, formulas, bonuses, deductions and reimbursements." },
      { icon: Shield, title: "Statutory compliance", description: "Income tax, social security, and country-specific filings." },
      { icon: Workflow, title: "Time + attendance", description: "Integration with biometric / app-based time tracking." },
      { icon: FileText, title: "Payslips + reports", description: "Automated payslips, year-end forms, and audit reports." },
      { icon: Calculator, title: "Journal automation", description: "Per-pay-run journals posted directly to GL." },
      { icon: Activity, title: "Multi-country support", description: "Country-specific rules and currencies handled cleanly." },
    ],
    process: [
      { step: "01", title: "Map structures", description: "Components, taxes, statutory rules, and policies." },
      { step: "02", title: "Configure", description: "Salary slabs, deductions, and approval flows." },
      { step: "03", title: "Pilot run", description: "Parallel-run with current payroll for reconciliation." },
      { step: "04", title: "Go live", description: "Cutover with full team training and support." },
    ],
    deliverables: ["Salary structure config", "Tax + statutory setup", "Time integration", "Payslip + report templates", "GL posting rules", "Training + SOPs"],
    techStack: ["ERPNext HR + Payroll", "Odoo Payroll", "Gusto", "Deel", "Rippling", "Biometric integrations"],
    pricing: [
      { name: "Small team", price: "$3.5k", features: ["Up to 25 employees", "1 country", "Time integration", "2 weeks"] },
      { name: "Full payroll", price: "$10k", period: "project", highlighted: true, features: ["100+ employees", "Multi-country", "Statutory + filings", "5–7 weeks"] },
      { name: "Payroll retainer", price: "$1.5k", period: "month", features: ["Run support", "Rule updates", "Statutory changes"] },
    ],
    faqs: [
      { q: "Multi-country?", a: "Yes — we set up country-specific rules with consolidated reporting." },
      { q: "Will it file taxes for us?", a: "We generate the filings; submission can be done by your team or partner (Gusto/Deel) where supported." },
    ],
  },
  "workflow-automation": {
    eyebrow: "ERP · Customization",
    title: "Workflow Automation",
    sub: "Automate approvals, recurring tasks and cross-module flows inside your ERP.",
    metaDesc: "ERP workflow automation: approvals, recurring tasks, conditional routing and cross-module process flows.",
    intro:
      "We automate the high-friction processes that keep teams stuck in email — multi-step approvals, recurring billing, document generation, conditional routing, escalations, and SLA-driven follow-ups.",
    features: [
      { icon: Workflow, title: "Approval chains", description: "Multi-step, conditional, and threshold-based approvals." },
      { icon: RefreshCw, title: "Recurring jobs", description: "Subscriptions, retainers, and scheduled task runs." },
      { icon: FileText, title: "Document generation", description: "Templated PDFs and contracts with e-signature." },
      { icon: Activity, title: "Escalations + SLAs", description: "Auto-escalate when SLAs slip; ping owners + managers." },
      { icon: Plug, title: "Cross-module flows", description: "Sales → inventory → finance flows triggered automatically." },
      { icon: Zap, title: "External triggers", description: "Webhooks, email parsing, and event-driven flows." },
    ],
    process: [
      { step: "01", title: "Map", description: "Top 10 manual processes and their owners." },
      { step: "02", title: "Prioritize", description: "Score by hours saved and error reduction." },
      { step: "03", title: "Build", description: "Implement, test, and pilot with end users." },
      { step: "04", title: "Iterate", description: "Measure adoption, fix friction, add coverage." },
    ],
    deliverables: ["Process inventory", "Top 10 automated workflows", "Document templates + e-sign", "Escalation + SLA setup", "Adoption metrics", "Training + SOPs"],
    techStack: ["ERPNext Workflows", "Odoo Studio", "n8n", "Zapier", "DocuSign", "Twilio"],
    pricing: [
      { name: "5 flows", price: "$3k", features: ["5 workflows", "Templates included", "1 review", "2 weeks"] },
      { name: "Full automation", price: "$10k", period: "project", highlighted: true, features: ["15+ flows", "Cross-module", "Escalations + SLAs", "4–6 weeks"] },
      { name: "Automation retainer", price: "$2k", period: "month", features: ["New flows monthly", "Maintenance", "Adoption tuning"] },
    ],
    faqs: [
      { q: "Inside the ERP or via n8n/Zapier?", a: "Inside the ERP when possible; external when the trigger or system is outside." },
      { q: "Will end users accept the change?", a: "Yes — we co-design with end users and pilot before rollout." },
    ],
  },
  "role-based-permissions": {
    eyebrow: "ERP · Customization",
    title: "Role-based Permissions",
    sub: "Right people, right access, audit-ready — without crippling the day-to-day.",
    metaDesc: "ERP role-based permissions: role design, segregation of duties, audit trails and SOX-aligned access controls.",
    intro:
      "We design role-based access that gives every team exactly what they need to do their job — and nothing more. Segregation of duties, audit trails, and SOX-aligned controls without a permissions matrix that requires a PhD.",
    features: [
      { icon: Lock, title: "Role design", description: "Roles aligned to job functions, not to individuals." },
      { icon: Shield, title: "Segregation of duties", description: "Conflicts prevented at the role and matrix level." },
      { icon: Activity, title: "Audit trail", description: "Every create/update/delete with user and timestamp." },
      { icon: Users, title: "Delegation + temp access", description: "Bounded delegation for leave, projects, and audits." },
      { icon: FileText, title: "Approval matrix", description: "Documented thresholds and escalation paths." },
      { icon: Search, title: "Access reviews", description: "Quarterly review reports and remediation tracking." },
    ],
    process: [
      { step: "01", title: "Workshop", description: "Job functions and current access pain points." },
      { step: "02", title: "Design", description: "Role catalog with SoD matrix and approval thresholds." },
      { step: "03", title: "Implement", description: "Configure roles, permissions, and audit logging." },
      { step: "04", title: "Govern", description: "Access review program and onboarding/offboarding flows." },
    ],
    deliverables: ["Role catalog + SoD matrix", "Approval threshold doc", "Audit logging setup", "Onboarding/offboarding playbook", "Access review program", "Training + SOPs"],
    techStack: ["ERPNext permissions", "Odoo security", "Okta / Entra ID", "Audit log tooling"],
    pricing: [
      { name: "Essentials", price: "$2.5k", features: ["5 roles", "Basic SoD", "Audit log", "1–2 weeks"] },
      { name: "Full RBAC", price: "$8k", period: "project", highlighted: true, features: ["Role catalog + SoD", "SSO/SAML", "Reviews + governance", "3–4 weeks"] },
      { name: "Governance retainer", price: "$1.5k", period: "month", features: ["Quarterly reviews", "New role design", "Audit support"] },
    ],
    faqs: [
      { q: "SOC 2 / SOX ready?", a: "Yes — controls and evidence aligned with both." },
      { q: "SSO integration?", a: "Yes — Okta, Entra ID, Google, and standard SAML/OIDC." },
    ],
  },
  "data-migration-setup": {
    eyebrow: "ERP · Customization",
    title: "Data Migration & Setup",
    sub: "Clean cutover from QuickBooks, Xero, Excel or legacy ERP — with reconciled balances.",
    metaDesc: "ERP data migration and setup: legacy system migration, cleansing, mapping, reconciliation and cutover.",
    intro:
      "We migrate from QuickBooks, Xero, NetSuite, Sage, legacy ERPs, or piles of spreadsheets — with field mapping, data cleansing, opening balance reconciliation, parallel runs, and a cutover plan that doesn't leave finance crying on Monday.",
    features: [
      { icon: Database, title: "Source analysis", description: "Inventory of source data, quality, and gaps." },
      { icon: Workflow, title: "Mapping + cleansing", description: "Field mapping, deduplication, and normalization." },
      { icon: Calculator, title: "Opening balances", description: "Reconciled AR/AP, trial balance, and inventory cost." },
      { icon: RefreshCw, title: "Parallel run", description: "Run both systems for one period to verify accuracy." },
      { icon: FileText, title: "Cutover plan", description: "Hour-by-hour go-live with rollback ready." },
      { icon: Search, title: "Reconciliation", description: "Post-cutover reconciliation and sign-off." },
    ],
    process: [
      { step: "01", title: "Audit source", description: "Pull data, profile quality, list gaps." },
      { step: "02", title: "Map + clean", description: "Mapping rules, cleansing scripts, and review." },
      { step: "03", title: "Trial migrate", description: "Test-load and reconcile with source totals." },
      { step: "04", title: "Cutover", description: "Final load, opening balances, and sign-off." },
    ],
    deliverables: ["Source profile + gap list", "Mapping + cleansing scripts", "Reconciled opening balances", "Cutover runbook", "Reconciliation report", "Sign-off package"],
    techStack: ["Custom Python ETL", "Pandas", "ERPNext / Odoo APIs", "Postgres", "Airbyte"],
    pricing: [
      { name: "Small", price: "$3k", features: ["<5k records", "Single module", "Standard mapping", "2 weeks"] },
      { name: "Standard", price: "$10k", period: "project", highlighted: true, features: ["Multi-module", "Cleansing + recon", "Parallel run", "4–6 weeks"] },
      { name: "Enterprise", price: "Custom", features: ["100k+ records", "Multi-entity", "Complex transforms", "Dedicated team"] },
    ],
    faqs: [
      { q: "How accurate is the migration?", a: "We target 100% reconciliation on financial balances; transactional data within agreed tolerances." },
      { q: "Can we keep the old system live?", a: "Yes — parallel runs are standard for one full close cycle." },
    ],
  },
  "training-support": {
    eyebrow: "ERP · Customization",
    title: "Training & Support",
    sub: "Make adoption stick with role-based training, documentation and ongoing help.",
    metaDesc: "ERP training and support: role-based training, documentation, helpdesk and continuous improvement.",
    intro:
      "ERP projects fail at adoption more than they fail at implementation. We deliver role-based training, write SOPs your team actually uses, run a responsive helpdesk, and keep the system improving as your business changes.",
    features: [
      { icon: GraduationCap, title: "Role-based training", description: "Tailored sessions per role with hands-on labs." },
      { icon: FileText, title: "SOPs + playbooks", description: "Written, video, and in-app help your team will use." },
      { icon: Users, title: "Train-the-trainer", description: "Build internal champions to scale knowledge." },
      { icon: Activity, title: "Helpdesk + SLAs", description: "Ticketing with response and resolution SLAs." },
      { icon: Workflow, title: "Adoption tracking", description: "Measure usage, identify drop-off, course-correct." },
      { icon: Wrench, title: "Continuous improvement", description: "Monthly tweaks based on user feedback." },
    ],
    process: [
      { step: "01", title: "Plan", description: "Role mapping, learning paths, and success metrics." },
      { step: "02", title: "Build", description: "Materials, videos, and in-app guides." },
      { step: "03", title: "Train", description: "Sessions + labs + champion enablement." },
      { step: "04", title: "Sustain", description: "Helpdesk, adoption tracking, and improvements." },
    ],
    deliverables: ["Role-based training sessions", "SOPs + video library", "Champion enablement", "Helpdesk with SLAs", "Adoption dashboard", "Continuous improvement plan"],
    techStack: ["Notion / Confluence", "Loom", "Intercom / Zendesk", "Userpilot / Pendo"],
    pricing: [
      { name: "Launch", price: "$2k", features: ["2 role sessions", "Basic SOPs", "30-day support", "1 week"] },
      { name: "Full enablement", price: "$8k", period: "project", highlighted: true, features: ["All roles", "Video + SOP library", "60-day support", "4 weeks"] },
      { name: "Support retainer", price: "$1.5k", period: "month", features: ["Helpdesk + SLAs", "Continuous improvements", "Quarterly trainings"] },
    ],
    faqs: [
      { q: "Onsite or remote training?", a: "Both — we prefer remote for repeatability; onsite for kickoff or large rollouts." },
      { q: "Languages?", a: "English, Spanish, Arabic, and Hindi natively; others on request." },
    ],
  },
};

export const Route = createFileRoute("/services/erp-solution/$child")({
  component: ChildPage,
  loader: ({ params }) => {
    const entry = CHILDREN[params.child];
    if (!entry) throw notFound();
    return { entry };
  },
  head: ({ params }) => {
    const entry = CHILDREN[params.child];
    if (!entry) return {};
    const url = `/services/erp-solution/${params.child}`;
    return {
      meta: [
        { title: `${entry.title} — CoderLity` },
        { name: "description", content: entry.metaDesc },
        { property: "og:title", content: `${entry.title} — CoderLity` },
        { property: "og:description", content: entry.metaDesc },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
});

function ChildPage() {
  const { entry } = Route.useLoaderData();
  return <ServicePage data={entry} />;
}
